import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth, diasParaVencer } from '../../contexts/AuthContext';
import { useData } from '../../contexts/DataContext';
import AdminLayout from '../../components/AdminLayout';
import { supabase } from '../../lib/supabase';
import { resumoAluna } from '../../lib/alunaInsights';

// Ícones lineares (estilo Lucide/Phosphor, 2px) usados nas Ações Rápidas —
// substituem os emojis pra bater com o design system v3.
const ACTION_ICONS = {
  treino: (
    <svg viewBox="0 0 24 24"><path d="M6.5 6.5L17.5 17.5M17.5 6.5L6.5 17.5" /><circle cx="12" cy="12" r="10" /></svg>
  ),
  dieta: (
    <svg viewBox="0 0 24 24"><path d="M12 2v20M2 12h20" /></svg>
  ),
  alunas: (
    <svg viewBox="0 0 24 24"><circle cx="12" cy="8" r="4" /><path d="M4 21c0-4 3.6-7 8-7s8 3 8 7" /></svg>
  ),
  mensagens: (
    <svg viewBox="0 0 24 24"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" /></svg>
  ),
  agenda: (
    <svg viewBox="0 0 24 24"><rect x="3" y="4" width="18" height="18" rx="3" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" /></svg>
  ),
  evolucao: (
    <svg viewBox="0 0 24 24"><path d="M3 3v18h18" /><path d="M7 15l4-4 3 3 5-6" /></svg>
  ),
  config: (
    <svg viewBox="0 0 24 24"><circle cx="12" cy="12" r="3" /><path d="M19.4 15a1.7 1.7 0 0 0 .3 1.9l.1.1a2 2 0 0 1-2.8 2.8l-.1-.1a1.7 1.7 0 0 0-1.9-.3 1.7 1.7 0 0 0-1 1.6V21a2 2 0 0 1-4 0v-.1a1.7 1.7 0 0 0-1-1.6 1.7 1.7 0 0 0-1.9.3l-.1.1A2 2 0 0 1 4.2 17l.1-.1a1.7 1.7 0 0 0 .3-1.9 1.7 1.7 0 0 0-1.6-1H3a2 2 0 0 1 0-4h.1a1.7 1.7 0 0 0 1.6-1 1.7 1.7 0 0 0-.3-1.9l-.1-.1A2 2 0 0 1 7 4.2l.1.1a1.7 1.7 0 0 0 1.9.3h.1a1.7 1.7 0 0 0 1-1.6V3a2 2 0 0 1 4 0v.1a1.7 1.7 0 0 0 1 1.6h.1a1.7 1.7 0 0 0 1.9-.3l.1-.1A2 2 0 0 1 19.8 7l-.1.1a1.7 1.7 0 0 0-.3 1.9v.1a1.7 1.7 0 0 0 1.6 1h.1a2 2 0 0 1 0 4H21a1.7 1.7 0 0 0-1.6 1Z" /></svg>
  ),
};

const T = {
  h1: { fontFamily:"'Inter', sans-serif", fontSize:'clamp(28px,4vw,40px)', fontWeight:700, color:'var(--text)', marginBottom:'4px' },
  em: { fontStyle:'italic', color:'var(--accent)' },
  sub: { fontSize:'14px', color:'var(--text-2)', marginBottom:'36px' },
  statCard: { background:'var(--surface)', border:'1px solid var(--divider)', padding:'24px 20px', borderRadius:'24px', boxShadow:'var(--sh-card)', cursor:'default' },
  statNum: { fontFamily:"'Inter', sans-serif", fontSize:'40px', fontWeight:700, color:'var(--text)', lineHeight:1, marginBottom:'4px' },
  statLabel: { fontSize:'11px', color:'var(--text-2)', letterSpacing:'0.08em', textTransform:'uppercase' },
  sectionLabel: { fontSize:'10px', fontWeight:700, letterSpacing:'0.14em', textTransform:'uppercase', color:'var(--accent)', marginBottom:'14px', display:'flex', alignItems:'center', gap:'10px' },
  sectionLine: { flex:1, height:'1px', background:'var(--divider)' },
  actionBtn: { padding:'20px', border:'1.5px solid var(--divider)', borderRadius:'var(--r-card)', background:'var(--surface-2)', cursor:'pointer', textAlign:'left', transition:'all 0.2s', fontFamily:'inherit' },
  actionIcon: { fontSize:'22px', marginBottom:'8px', display:'block' },
  actionTitle: { fontSize:'14px', fontWeight:600, color:'var(--text)', marginBottom:'2px' },
  actionDesc: { fontSize:'12px', color:'var(--text-2)' },
  listItem: { padding:'14px 18px', background:'var(--surface)', border:'1px solid var(--divider)', borderRadius:'var(--r-btn)', marginBottom:'6px', display:'flex', justifyContent:'space-between', alignItems:'center', boxShadow:'var(--sh-card)' },
  badge: { padding:'3px 10px', background:'rgba(137,91,64,0.1)', color:'var(--accent)', borderRadius:'100px', fontSize:'11px', fontWeight:600 },
};

export default function AdminDashboard() {
  const { user, alunas } = useAuth();
  const { treinos, dietas, agendamentos, checkins, exames, feedbacksTreino } = useData();
  const navigate = useNavigate();
  const [msgNaoLidas, setMsgNaoLidas] = useState(0);
  const [treinosSemana, setTreinosSemana] = useState(0);

  useEffect(() => {
    // Mensagens não lidas (de alunas para admin)
    supabase.from('mensagens')
      .select('id', { count: 'exact' })
      .eq('remetente', 'aluna')
      .eq('lida', false)
      .then(({ count }) => setMsgNaoLidas(count || 0));

    // Treinos feitos essa semana
    const hoje = new Date();
    const inicioDaSemana = new Date(hoje);
    inicioDaSemana.setDate(hoje.getDate() - hoje.getDay());
    inicioDaSemana.setHours(0,0,0,0);
    supabase.from('checkins')
      .select('id', { count: 'exact' })
      .gte('criado_em', inicioDaSemana.toISOString())
      .then(({ count }) => setTreinosSemana(count || 0));
  }, []);

  const pendentes = agendamentos.filter(a => a.status === 'pendente').length;
  const alunaAtivas = alunas.filter(a => a.status === 'ativa' && a.role !== 'admin').length;
  const alunasVencendo = alunas.filter(a => {
    const d = diasParaVencer(a.inicioPlano, a.plano);
    return d !== null && d <= 7 && d >= 0 && a.status === 'ativa';
  });
  const alunasPausadas = alunas.filter(a => a.status === 'pausada' && a.role !== 'admin').length;
  const prioridades = alunas
    .filter(a => a.role !== 'admin')
    .map(aluna => ({ aluna, resumo: resumoAluna(aluna, { treinos, dietas, agendamentos, checkins, exames, feedbacksTreino }) }))
    .filter(item => item.resumo.prioridade > 0)
    .sort((a, b) => b.resumo.prioridade - a.resumo.prioridade)
    .slice(0, 8);

  // Últimos check-ins com nome da aluna
  const ultimosCheckins = [...checkins]
    .sort((a,b) => new Date(b.criadoEm) - new Date(a.criadoEm))
    .slice(0, 5)
    .map(ck => ({
      ...ck,
      alunaNome: alunas.find(a => a.id === ck.alunaId)?.name || 'Aluna',
      treinoTitulo: treinos.find(t => t.id === ck.treinoId)?.titulo || 'Treino',
      concluidos: ck.exercicios?.filter(e => e.concluido).length || 0,
      total: ck.exercicios?.length || 0,
    }));

  const formatDate = (iso) => {
    const d = new Date(iso);
    const hoje = new Date();
    if (d.toDateString() === hoje.toDateString()) return 'Hoje';
    const ontem = new Date(hoje); ontem.setDate(hoje.getDate() - 1);
    if (d.toDateString() === ontem.toDateString()) return 'Ontem';
    return d.toLocaleDateString('pt-BR', { day:'2-digit', month:'2-digit' });
  };

  return (
    <AdminLayout msgNaoLidas={msgNaoLidas}>
      <style>{`
        .dash-grid4 { display:grid; grid-template-columns:repeat(4,1fr); gap:12px; margin-bottom:36px; }
        .dash-grid2 { display:grid; grid-template-columns:repeat(2,1fr); gap:12px; margin-bottom:36px; }
        .dash-actions { display:grid; grid-template-columns:repeat(2,1fr); gap:10px; margin-bottom:32px; }
        .dash-action-btn:hover { border-color:var(--accent) !important; background:var(--bg) !important; }
        .stat-card-clickable { cursor:pointer; transition:all 0.2s; }
        .stat-card-clickable:hover { border-color:var(--accent) !important; transform:translateY(-1px); }
        .priority-list{display:grid;gap:8px;margin-bottom:32px}.priority-row{width:100%;display:grid;grid-template-columns:minmax(150px,1fr) minmax(220px,2fr) auto;gap:14px;align-items:center;padding:14px 16px;border:1px solid var(--divider);border-radius:16px;background:var(--surface);text-align:left;cursor:pointer}.priority-row:hover{border-color:var(--accent)}.priority-name{font-size:13px;font-weight:700;color:var(--text)}.priority-alert{font-size:12px;color:var(--text-2)}.priority-count{font-size:11px;font-weight:700;color:#C62828;background:rgba(229,57,53,.08);padding:5px 9px;border-radius:999px}
        @media(max-width:700px){ .dash-grid4{ grid-template-columns:1fr 1fr; } .dash-grid2{ grid-template-columns:1fr 1fr; } .dash-actions{ grid-template-columns:1fr; } }
        @media(max-width:700px){.priority-row{grid-template-columns:1fr auto}.priority-alert{grid-column:1/-1}}
        @media(max-width:400px){ .dash-grid4{ grid-template-columns:1fr 1fr; } }
      `}</style>

      <h1 style={T.h1}>Olá, <em style={T.em}>{user?.name?.split(' ')[0]}</em></h1>
      <p style={T.sub}>Painel do Team Carol Macedo</p>

      {/* NÚMEROS PRINCIPAIS */}
      <div className="dash-grid4">
        <div className="stat-card-clickable" style={T.statCard} onClick={() => navigate('/admin/alunas')}>
          <div style={{ ...T.statNum, color:'var(--text)' }}>{alunaAtivas}</div>
          <div style={T.statLabel}>Alunas ativas</div>
        </div>
        <div className="stat-card-clickable" style={T.statCard} onClick={() => navigate('/admin/alunas')}>
          <div style={{ ...T.statNum, color: alunasPausadas > 0 ? '#E53935' : 'var(--text)' }}>{alunasPausadas}</div>
          <div style={T.statLabel}>Pausadas</div>
        </div>
        <div style={T.statCard}>
          <div style={{ ...T.statNum, color:'var(--accent)' }}>{treinosSemana}</div>
          <div style={T.statLabel}>Treinos esta semana</div>
        </div>
        <div className="stat-card-clickable" style={T.statCard} onClick={() => navigate('/admin/mensagens')}>
          <div style={{ ...T.statNum, color: msgNaoLidas > 0 ? 'var(--accent)' : 'var(--text)', display:'flex', alignItems:'center', gap:'8px' }}>
            {msgNaoLidas}
            {msgNaoLidas > 0 && <span style={{ width:'10px', height:'10px', borderRadius:'50%', background:'var(--accent)', display:'inline-block' }} />}
          </div>
          <div style={T.statLabel}>Msgs não lidas</div>
        </div>
      </div>

      {/* PRIORIDADES DO DIA */}
      <div style={T.sectionLabel}>Prioridades de hoje <div style={T.sectionLine} /></div>
      <div className="priority-list">
        {prioridades.length ? prioridades.map(({ aluna, resumo }) => (
          <button key={aluna.id} className="priority-row" onClick={() => navigate(`/admin/alunas/${aluna.id}`)}>
            <span className="priority-name">{aluna.name}</span>
            <span className="priority-alert">{resumo.alerts.slice(0, 2).map(alert => alert.text).join(' · ')}</span>
            <span className="priority-count">{resumo.alerts.length} alerta{resumo.alerts.length !== 1 ? 's' : ''}</span>
          </button>
        )) : <div style={{...T.listItem, color:'var(--text-2)', fontSize:'13px'}}>Nenhuma prioridade importante no momento.</div>}
      </div>

      {/* ALERTA PLANOS VENCENDO */}
      {alunasVencendo.length > 0 && (
        <div style={{ background:'rgba(229,57,53,0.05)', border:'1px solid rgba(229,57,53,0.2)', borderRadius:'20px', padding:'16px 20px', marginBottom:'24px' }}>
          <div style={{ fontSize:'11px', fontWeight:700, letterSpacing:'0.1em', textTransform:'uppercase', color:'#E53935', marginBottom:'10px' }}>
            ⚠ Planos vencendo em até 7 dias
          </div>
          {alunasVencendo.map(a => {
            const dias = diasParaVencer(a.inicioPlano, a.plano);
            return (
              <div key={a.id} style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'8px 0', borderBottom:'1px solid rgba(229,57,53,0.1)', fontSize:'13px' }}>
                <span style={{ fontWeight:500, color:'var(--text)' }}>{a.name}</span>
                <div style={{ display:'flex', alignItems:'center', gap:'12px' }}>
                  <span style={{ fontSize:'12px', color:'#E53935', fontWeight:600 }}>{dias === 0 ? 'Vence hoje' : `${dias}d`}</span>
                  <a href={`https://wa.me/${(a.telefone||'').replace(/\D/g,'')}?text=${encodeURIComponent('Olá ' + a.name.split(' ')[0] + ', seu plano vence em breve! Vamos renovar?')}`}
                    target="_blank" rel="noreferrer"
                    style={{ fontSize:'11px', fontWeight:700, color:'#25D366', textDecoration:'none' }}>
                    WhatsApp
                  </a>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* AÇÕES RÁPIDAS */}
      <div style={T.sectionLabel}>Ações rápidas <div style={T.sectionLine} /></div>
      <div className="dash-actions" style={{ marginBottom:'32px' }}>
        {[
          { icon:'treino', title:'Novo treino', desc:'Cadastrar treino para uma aluna', path:'/admin/treino/novo' },
          { icon:'dieta', title:'Novo plano alimentar', desc:'Cadastrar nutrição para uma aluna', path:'/admin/dieta/nova' },
          { icon:'alunas', title:'Alunas', desc:'Buscar e gerenciar alunas', path:'/admin/alunas' },
          { icon:'mensagens', title:'Mensagens', desc: msgNaoLidas > 0 ? `${msgNaoLidas} não lida(s)` : 'Nenhuma nova', path:'/admin/mensagens' },
          { icon:'agenda', title:'Agendamentos', desc: pendentes > 0 ? `${pendentes} pendente(s)` : 'Nenhum pendente', path:'/admin/agendamentos' },
          { icon:'evolucao', title:'Evolução física', desc:'Ver medidas das alunas', path:'/admin/evolucao' },
          { icon:'config', title:'Configurações', desc:'Modalidades e lembretes do app', path:'/admin/configuracoes' },
        ].map((a, i) => (
          <button key={i} className="dash-action-btn" style={T.actionBtn} onClick={() => navigate(a.path)}>
            <span className="list-icon" style={{ marginBottom:'10px' }}>{ACTION_ICONS[a.icon]}</span>
            <div style={T.actionTitle}>{a.title}</div>
            <div style={T.actionDesc}>{a.desc}</div>
          </button>
        ))}
      </div>

      {/* ÚLTIMOS CHECK-INS */}
      {ultimosCheckins.length > 0 && (
        <>
          <div style={T.sectionLabel}>Treinos recentes das alunas <div style={T.sectionLine} /></div>
          {ultimosCheckins.map((ck, i) => (
            <div key={i} style={T.listItem}>
              <div>
                <div style={{ fontSize:'14px', fontWeight:600, color:'var(--text)' }}>{ck.alunaNome}</div>
                <div style={{ fontSize:'12px', color:'var(--text-2)', marginTop:'2px' }}>
                  {ck.treinoTitulo} · {ck.concluidos}/{ck.total} exercícios
                  {ck.nota_geral && <span style={{ marginLeft:'6px' }}>{ck.nota_geral==='facil'?'😊':ck.nota_geral==='ok'?'😐':'😓'}</span>}
                </div>
              </div>
              <div style={{ ...T.badge, background: ck.concluidos===ck.total?'rgba(46,125,50,0.1)':'rgba(137,91,64,0.1)', color: ck.concluidos===ck.total?'#2E7D32':'var(--accent)' }}>
                {formatDate(ck.criadoEm)}
              </div>
            </div>
          ))}
        </>
      )}
    </AdminLayout>
  );
}
